/** Cmd/Ctrl+K and Cmd/Ctrl+F routing for Quantis search, gated by the person's Advanced preferences. */
import { useEffect } from 'react';
import { getPreferencesSnapshot } from '../../features/preferences/preferencesStore';

export interface SearchHotkeyEventLike {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  isComposing?: boolean;
}

export type SearchHotkey = 'quick-search' | 'find-in-page';

/** Which Quantis search shortcut an event is, if its preference is on. Ctrl+Cmd+F (takeoff) is deliberately not matched. */
export function matchSearchHotkey(
  event: SearchHotkeyEventLike,
  advanced: { cmdKSearch: boolean; cmdFSearch: boolean },
): SearchHotkey | null {
  if (event.isComposing || event.shiftKey || event.altKey) return null;
  // Exactly one of Cmd and Ctrl: both together belongs to other tools.
  if (event.metaKey === event.ctrlKey) return null;
  const key = event.key?.toLowerCase();
  if (key === 'k') return advanced.cmdKSearch ? 'quick-search' : null;
  if (key === 'f') return advanced.cmdFSearch ? 'find-in-page' : null;
  return null;
}

/** Focuses the header search field, returning false when none is on screen. */
export function focusHeaderSearch(): boolean {
  const input = document.querySelector<HTMLInputElement>('[data-slot="unified-header-search"] input');
  if (!input) return false;
  input.focus();
  input.select();
  return true;
}

/** Mounted once in the shell for Cmd/Ctrl+F. Cmd/Ctrl+K is bound by the shortcut registry (useGlobalShortcuts). */
export function useSearchHotkeys(): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (matchSearchHotkey(event, getPreferencesSnapshot().advanced) !== 'find-in-page') return;
      // With no search field on screen the browser keeps its own shortcut.
      if (focusHeaderSearch()) event.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}
